import {Log} from '@aboutyou/mlm';
import {useEffect, useRef, useState} from 'react';
import {
  useDataFetcherAction,
  useDataFetcherInitialData,
  useIsInitialServerRequest,
} from './context';
import {Result, SuccessResult} from './types';
import {isValidationError, throwValidatedDecorator} from './throwable';
import {
  useQuery,
  UseQueryOptions,
  DEFAULT_QUERY_CONFIG,
  updateCacheServer,
  useQueryClient,
  UseQueryResultWithUpdateCache,
  refetchServer,
} from './react-query';

import {hasUnresolvedStream, isEmptyStream} from './stream/utils';
import {useUpdateQueryCache} from './useUpdateQueryCache';
import {exists, useForceUpdate} from '@aboutyou/utils';
import {useRequestNonCriticalErrorMessagePatterns} from './grpc/GrpcClientContext';

export interface Config<T> {
  /**
   * called once right after the component is rendered and the initial state is fetched from the server
   */
  hydrate?: (data: Result<T>) => T;

  staleTime?: UseQueryOptions['staleTime'];
  cacheTime?: UseQueryOptions['cacheTime'];
  refetchInterval?: UseQueryOptions['refetchInterval'];
  refetchOnMount?: UseQueryOptions['refetchOnMount'];
  retry?: UseQueryOptions['retry'];
  /**
   * Allows to keep the state that was available before a new loading is triggered to e.g. not annoy the user with loading spinner and flickering. It can take 3 different states. `true` (default) it will keep the state as long as the new data is loaded. `false` it will switch immediate to show the loading state when the data fetching is triggered. `keep-until-frustrating` will keep the state for 500ms active and switches if the request takes too long.
   */
  keepPreviousData?:
    | UseQueryOptions['keepPreviousData']
    | 'keep-until-frustrating';

  onSuccess?: UseQueryOptions<T>['onSuccess'];

  onInitialDataSuccess?: (data: T) => void;

  onError?: UseQueryOptions<T>['onError'];

  disabled?: boolean;
  lowPriority?: boolean;

  /**
   * if `nonEssential` is set to `true` the error for this request will not be considered as a critical error and will not be logged to Monitoring
   */
  nonEssential?: boolean;

  validateData?: (data: T) => void;
  validateError?: (error: unknown) => void;
  /**
   * Will make sure this request will not fail on server side if a HEAD request is made. This should only be activated if no `validateData` or `validateError` handler is used and status codes might be updated based on an element rendered within the data fetcher of this request.
   */
  forceRenderForHeadRequests?: boolean;

  /**
   * `syncQueryCall` allows to call the query function synchronously during the SSR first render pass, ie: isInitialServerRequest === true.
   * This is useful if you want to call the query function during the first render pass in addition to the actual render with resolved data.
   * This can be used to partially nest useRequest calls, where the inner useRequest depends on the outer useRequest to return a synchronous result during the first render pass.
   * Example: useCookie() giving correct value during the first render pass for a useGrpcRequest() which depends on the cookie value.
   */
  syncQueryCall?: boolean;
  /**
   * Every time this token changes a refetch will be triggered automatically.
   * In combination with `keepPreviousData` this e.g. allow to avoid loading states and
   * update in the background even after the user leaving the current page but coming back later.
   */
  refetchToken?: string[] | string;
}

export const useRequest = <T extends object>(
  queryKey: string | string[],
  fetcherFunction: () => Promise<T> | T,
  config?: Config<T>,
): DataFetcherRequest<T & {validated?: Record<string, unknown> | null}> => {
  const previousStreamData = useRef<T[] | undefined>();
  const cacheKey = generateCacheKey(queryKey);
  const result = useDataFetcherInitialData<T>(cacheKey);
  const nonCriticalErrorMessagePatterns =
    useRequestNonCriticalErrorMessagePatterns();
  const {addRequest, deleteKey} = useDataFetcherAction();

  if (!process.browser) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isInitialServerRequest = useIsInitialServerRequest();

    if (config?.disabled) {
      return {
        ...result,
        isLoading: false,
        isStale: false,
        isIdle: true,
        isPreviousData: false,
        isFetching: false,
        isFetched: false,
        updateCache: updateCacheServer,
        refetch: refetchServer,
      };
    }

    if (isInitialServerRequest) {
      // on the server this is just a function that will be called once
      // it is therefore okay to trigger a side effect
      addRequest(
        cacheKey,
        throwValidatedDecorator(
          fetcherFunction,
          config,
          nonCriticalErrorMessagePatterns,
        ),
        config,
      );

      if (config?.syncQueryCall) {
        return {
          ...result,
          isLoading: false,
          isStale: false,
          isIdle: false,
          isPreviousData: false,
          isFetching: false,
          isFetched: true,
          updateCache: updateCacheServer,
          refetch: refetchServer,
          data: fetcherFunction() as T,
        };
      }

      return {
        ...result,
        isLoading: true,
        isStale: false,
        isIdle: false,
        isPreviousData: false,
        isFetched: false,
        isFetching: true,
        updateCache: updateCacheServer,
        refetch: refetchServer,
      };
    }

    return {
      ...result,
      isLoading: !result,
      isStale: !result,
      isIdle: false,
      isPreviousData: false,
      isFetched: !!result,
      isFetching: !result,
      updateCache: updateCacheServer,
      refetch: refetchServer,
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queryClient = useQueryClient();
  const hasInitialData = exists(result) && 'data' in result;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const initialError = useRef(
    exists(result) && 'error' in result ? result.error : null,
  );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const updateCache = useUpdateQueryCache({key: cacheKey});

  const {hydrate, ...queryConfig} = {
    hydrate(result: SuccessResult<T>) {
      return result.data;
    },
    enabled: !config?.disabled,
    ...DEFAULT_QUERY_CONFIG,
    ...config,
  };

  // if a component is rendered multiple times in the same session the state might have already mutated. We should therefore delete it after hydration to not get outdated data for some reason
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => deleteKey(cacheKey), [deleteKey, cacheKey]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useUpdateInitialStateFix({
    hasInitialData,
    cacheKey,
    queryConfig,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (hasInitialData) {
      // call onInitialDataSuccess once when the server data is hydrated
      if (config?.onInitialDataSuccess) {
        config.onInitialDataSuccess(result.data as T);
      }
    }
  }, [config, hasInitialData]);

  const initialData = (() => {
    if (hasInitialData) {
      return hydrate(result as SuccessResult<T>);
    }
    return undefined;
  })();

  // the name might eventually collapse with the one in useQuery API
  const pureQueryConfig = {...queryConfig};
  delete pureQueryConfig.validateData;
  delete pureQueryConfig.validateError;
  delete pureQueryConfig.disabled;

  let shouldKeepPreviousUntilFrustrating = false;
  if (pureQueryConfig.keepPreviousData === 'keep-until-frustrating') {
    shouldKeepPreviousUntilFrustrating = true;
    pureQueryConfig.keepPreviousData = true;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queryResult = useQuery<T, Error>(
    cacheKey,
    throwValidatedDecorator(
      fetcherFunction,
      config,
      nonCriticalErrorMessagePatterns,
    ),
    {
      initialData,
      ...(pureQueryConfig as UseQueryOptions<T, Error>),
      onError(error: Error) {
        initialError.current = null;
        if (pureQueryConfig?.onError) {
          pureQueryConfig?.onError?.(error);
          return;
        }
        if (isValidationError(error)) {
          return;
        }
        logClientError({cacheKeys: getHumanReadableKey(cacheKey), error});
      },
      onSuccess(...args) {
        initialError.current = null;
        pureQueryConfig?.onSuccess?.(...args);
      },
    },
  ) as DataFetcherRequest<T & {validated?: Record<string, unknown> | null}>;
  queryResult.updateCache = updateCache;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const forceLoadingState = useKeepUntilFrustrating<T>({
    cacheKey,
    queryResult,
    shouldKeepPreviousUntilFrustrating,
    disabled: queryConfig.disabled,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const lastTokenKey = `${cacheKey}-tokenKey`;
    if (config?.refetchToken !== undefined) {
      const lastToken = queryClient.getQueryData(lastTokenKey);
      if (lastToken !== config.refetchToken) {
        if (lastToken !== undefined) {
          queryResult.refetch();
        }
        queryClient.setQueryDefaults(lastTokenKey, {
          cacheTime: Infinity,
        });
        queryClient.setQueryData(lastTokenKey, config.refetchToken);
      }
    }
  }, [config?.refetchToken, cacheKey, queryClient]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (hasUnresolvedStream(queryResult.data!)) {
      queryResult.data.stream.subscribe({
        next: data => {
          queryClient.setQueryData<{
            items: T[];
            trailers: Record<string, string[]>;
          }>(cacheKey, currentQueryData => {
            const items = currentQueryData!.items.concat(data);
            return {items, trailers: currentQueryData?.trailers ?? {}};
          });
        },
        error: async (error: Error) => {
          logClientError({cacheKeys: getHumanReadableKey(cacheKey), error});
        },
        complete: () => {
          // if the stream has ended and was empty
          // update query data not to be undefined
          // this will internally make query successful and set isLoading to false
          if (isEmptyStream(queryClient.getQueryData(cacheKey))) {
            previousStreamData.current = undefined;

            queryClient.setQueryData<{items: T[]}>(cacheKey, () => ({
              items: [],
            }));
          }
        },
      });
    }
  }, [cacheKey, queryClient, queryResult.data]);

  // Preserve the items object from the stream request if keepPreviousData is true
  if (
    config?.keepPreviousData &&
    queryResult.data &&
    'items' in queryResult.data &&
    (queryResult.data.items as T[]).length > 0
  ) {
    previousStreamData.current = queryResult.data.items as T[];
  }

  if (hasUnresolvedStream(queryResult.data)) {
    if (config?.keepPreviousData && previousStreamData.current) {
      return {
        ...queryResult,
        data: {
          ...queryResult.data,
          items: previousStreamData.current,
        },
        isLoading: false,
      };
    }

    return {
      ...queryResult,
      data: undefined,
      isLoading: true,
    };
  }

  if (forceLoadingState) {
    return {
      ...queryResult,
      data: undefined,
      isLoading: true,
    };
  }

  if (initialError.current && !queryResult.data) {
    return {
      ...queryResult,
      isLoading: false,
      error: initialError.current,
    };
  }
  return queryResult;
};

export const generateCacheKey = (queryKey: string | string[]) => {
  return JSON.stringify(queryKey);
};

export const getHumanReadableKey = (
  cacheKey: string,
): {cacheKey: string; humanReadableCacheKey: string} => {
  try {
    // example cacheKey: "aysa_api.services.category_page.v1.stream.CategoryStreamService/GetProductStream:6764702873417" => in local development
    // example cacheKey: "@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/meta/service.grpc::ArticleDetailMetaService_GetPageMetaData:5131702094555" => in production
    const parsedKey = JSON.parse(cacheKey);
    const humanReadableCacheKey =
      parsedKey.split(':').reverse()[1]?.split('/').reverse()[0] ?? parsedKey;

    return {cacheKey: parsedKey, humanReadableCacheKey};
  } catch (error) {
    return {cacheKey, humanReadableCacheKey: cacheKey};
  }
};

export interface DataFetcherRequest<Response extends object, Error = unknown> {
  data?: Response;
  error?: Error;
  isLoading: boolean;
  isStale: boolean;
  isIdle: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isPreviousData: boolean;
  refetch: UseQueryResultWithUpdateCache<Response, Error>['refetch'];
  updateCache: (data: Response | undefined) => void;
}

const logClientError = async ({
  error,
  cacheKeys,
}: {
  cacheKeys: {
    cacheKey: string;
    humanReadableCacheKey: string;
  };
  error: unknown;
}) => {
  if (process.browser && error instanceof Error) {
    if (!isNetworkError(error)) {
      Log.error(
        '@aboutyou/data-fetcher',
        `useRequest error: ${cacheKeys.humanReadableCacheKey}`,
        {
          error,
          cacheKey: cacheKeys.cacheKey,
          humanReadableCacheKey: cacheKeys.humanReadableCacheKey,
        },
      );
    }
  }
};

function isNetworkError(error: Error) {
  return !!error.message.match(/network|fetch|request/i);
}

const FRUSTRATING_TIMEOUT = 500;
const useKeepUntilFrustrating = <TData extends object>({
  cacheKey,
  queryResult,
  shouldKeepPreviousUntilFrustrating,
  disabled,
}: {
  cacheKey: string;
  queryResult: DataFetcherRequest<TData, unknown>;
  shouldKeepPreviousUntilFrustrating: boolean;
  disabled?: boolean;
}) => {
  const [forceLoadingState, setForceLoadingState] = useState(false);

  useEffect(() => {
    if (!shouldKeepPreviousUntilFrustrating) {
      return;
    }
    const id = setTimeout(() => {
      if (queryResult.isPreviousData && !disabled) {
        // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
        setForceLoadingState(true);
      }
    }, FRUSTRATING_TIMEOUT);
    return () => {
      clearTimeout(id);
      setForceLoadingState(false);
    };
  }, [
    cacheKey,
    queryResult.isPreviousData,
    shouldKeepPreviousUntilFrustrating,
    disabled,
  ]);

  return forceLoadingState;
};

/**
 * When we use the server-rendered as `initialData` for react-query, there are issues going back and forth between the server rendered and the client rendered pages.
 * react-query has trouble going back to the hydrated state after navigating to a new page and then going back to the previous page.
 * more details in this github issue: https://github.com/tannerlinsley/react-query/issues/1859#issuecomment-827058020
 * This is a workaround to make sure that the hydrated state is reused when the user navigates back to the server rendered page after navigating to a new page.
 * Hint: This hook will not the same in development and production mode. `useEffect` is triggered multiple times in development and therefore the `setQueryData` call is done on initial mount as well. It still works as expected and would not cause any visual regressions. In react it's always recommended to test performance in production mode, so this is valid to accept.
 */
const useUpdateInitialStateFix = ({
  hasInitialData,
  cacheKey,
  queryConfig,
}: {
  hasInitialData: boolean;
  cacheKey: string;
  queryConfig: {
    keepPreviousData: Config<any>['keepPreviousData'];
    staleTime: Config<any>['staleTime'];
  };
}) => {
  const forceUpdate = useForceUpdate();
  const queryClient = useQueryClient();
  const initialCacheKeyToBeResetAfterHydration = useRef<string | null>(null);
  useEffect(() => {
    if (!hasInitialData) {
      return;
    }
    return () => {
      // whenever the initial mounted hook changes, register the initial key
      initialCacheKeyToBeResetAfterHydration.current = cacheKey;
    };
  }, [cacheKey, hasInitialData]);

  // only apply the fix if `keepPreviousData` and `staleTime` is set, as otherwise it would work as expected without it.
  const shouldUpdateInitialState =
    queryConfig.keepPreviousData && queryConfig.staleTime;

  useEffect(() => {
    if (
      shouldUpdateInitialState &&
      initialCacheKeyToBeResetAfterHydration.current === cacheKey
    ) {
      // This call is expensive and is therefore deferred as much as possible, so it's called when the initial state is mounted again (after it was unmounted within the same `useQuery` hook before)
      queryClient.setQueryData(cacheKey, (current: unknown) => current);
      initialCacheKeyToBeResetAfterHydration.current = null;
      // as `setQueryData` mutates the query cache it needs to force update the component. In most cases this would happen automatically for other unrelated changes, so it's not a big overhead in a normal application as all updates are applied in sync
      forceUpdate();
    }
  }, [
    cacheKey,
    shouldUpdateInitialState,
    queryClient,
    initialCacheKeyToBeResetAfterHydration,
    forceUpdate,
  ]);
};
